import React from 'react';
import { useTranslation } from 'react-i18next';
import { RentingsParams } from '../api/rentingsApi';
import Select from '@/components/form/Select';
import { ColumnFilter } from '@/components/ui/table/ColumnFilter';
import { CarWithLoadedModel } from '@/features/cars/api/carsApi';
import Button from '@/components/ui/button/Button';

interface RentingFiltersProps {
  currentFilters: Partial<RentingsParams>;
  onFilterChange: (filters: Partial<RentingsParams>) => void;
  cars?: CarWithLoadedModel[];
  isLoadingCars: boolean;
}

const RentingFilters: React.FC<RentingFiltersProps> = ({
  currentFilters,
  onFilterChange,
  cars,
  isLoadingCars
}) => {
  const { t } = useTranslation();
  const statusOptions = [
    { value: '', label: t('rentings.filters.status.all') },
    { value: 'draft', label: t('rentings.filters.status.draft') },
    { value: 'confirmed', label: t('rentings.filters.status.confirmed') },
    { value: 'cancelled', label: t('rentings.filters.status.cancelled') },
  ];

  const carOptions = [
    { value: '', label: t('rentings.filters.car.all') },
    ...(cars?.map(car => ({
      value: car.id,
      label: `${car.model?.brand_name} ${car.model?.model_name} ${car.registration_number}`,
    })) || []),
  ];

  const handleResetFilters = () => {
    onFilterChange({
      status: undefined,
      car: undefined,
      client: undefined,
    });
  };

  // Проверяем, есть ли активные фильтры
  const hasActiveFilters = currentFilters.status || currentFilters.car;

  return (
    <div className="flex gap-4 flex-wrap items-center">
      <div className="ml-0">
        <ColumnFilter
          size="sm"
          buttonText={t('filters.columns')}
          className="w-auto"
        />
      </div>
      <Select
        value={currentFilters.status || ''}
        onChange={(value) => onFilterChange({ ...currentFilters, status: value || undefined })}
        options={statusOptions}
        className="w-full"
        placeholder={t('rentings.filters.status.placeholder')}
      />
      <Select
        value={currentFilters.car || ''}
        onChange={(value) => onFilterChange({ ...currentFilters, car: value || undefined })}
        options={carOptions}
        className="w-full"
        placeholder={isLoadingCars ? t('rentings.filters.loading_cars') : t('rentings.filters.car.placeholder')}
      />
      {hasActiveFilters && (
        <Button
          variant="outline"
          size="sm"
          onClick={handleResetFilters}
          className="whitespace-nowrap flex items-center gap-1 !p-3"
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M18 6L6 18M6 6L18 18"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          {t('rentings.filters.reset')}
        </Button>
      )}
    </div>
  );
};

export default RentingFilters;
